import { notification } from "antd";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import { FC, memo, useCallback, useMemo } from "react";
import { CheckoutProduct, OrderType, PaymentForm } from "tm-shared/types";

import FlexContainer from "@/components/FlexContainer";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { setCheckoutActiveCollapse } from "@/store/slices/ui";
import { copyObjectToClipboard } from "@/utils/dom/copyObjectToClipboard";

import { CheckoutBody } from "../CheckoutBody";
import { CheckoutHeader } from "../CheckoutHeader";
import { StyledCACheckbox, StyledCACollapse } from "./Styled";

export interface ICheckoutAccordion {
    index: number;
    closed: boolean;
    invoiceId: string;
    orderId: string;
    marketId: string;
    products: { [barcode: string]: CheckoutProduct };
    ignoreRemainings: boolean;
    orderType: OrderType;
    address: string;
    contragent: string;
    companyName: string;
    paymentForm: PaymentForm;
    isChosen: boolean;
    isCarrier: boolean;
    isSelfDelivery: boolean;
    isPayOnDelivery: boolean;
    payOnDeliveryComment: string;
    paymentDate: string;
    orderDate: string;
    shipmentDate: string;
    customShipmentDate: string;
    isOfflineOrder: boolean;
    handleChangeCheckoutChoice: (
        e: CheckboxChangeEvent,
        orderId: string,
    ) => void;
    handleResetCheckouts: () => void;
}

const CheckoutAccordion: FC<ICheckoutAccordion> = ({
    closed,
    index,
    invoiceId,
    orderId,
    marketId,
    products,
    isCarrier,
    isSelfDelivery,
    isPayOnDelivery,
    ignoreRemainings,
    isChosen,
    orderType,
    paymentDate,
    orderDate,
    shipmentDate,
    customShipmentDate,
    address,
    contragent,
    companyName,
    paymentForm,
    payOnDeliveryComment,
    isOfflineOrder,
    handleChangeCheckoutChoice,
    handleResetCheckouts,
}) => {
    const dispatch = useAppDispatch();

    const { id: userId, phone: contactId } = useAppSelector((s) => s.user);
    const { offlineMode } = useAppSelector((s) => s.offline);
    const { activeCollapse } = useAppSelector((s) => s.ui.checkout);

    const totalSum = useMemo(
        () =>
            Object.values(products)
                .reduce(
                    (sum, product) => sum + product.price * product.count,
                    0,
                )
                .toFixed(2),
        [products],
    );

    const clipboardData = useMemo(
        () => ({
            invoiceId,
            orderId,
            marketId,
            userId,
            contactId,
            amount: totalSum,
            closed,
            paymentForm,
            context: {
                isCarrier,
                isSelfDelivery,
                isPayOnDelivery,
                ignoreRemainings,
            },
        }),
        [
            invoiceId,
            orderId,
            marketId,
            userId,
            contactId,
            totalSum,
            closed,
            paymentForm,
            isCarrier,
            isSelfDelivery,
            isPayOnDelivery,
            ignoreRemainings,
        ],
    );

    const handleCopyCheckoutInfo = useCallback(() => {
        copyObjectToClipboard(clipboardData);
    }, [clipboardData]);

    const handleCheckboxChange = useCallback(
        (e: CheckboxChangeEvent) => {
            if (offlineMode) {
                notification.error({
                    message:
                        "В оффлайн режимі неможливо здійснити оформлення замовлення!",
                });
            } else {
                handleChangeCheckoutChoice(e, orderId);
            }
        },
        [handleChangeCheckoutChoice, offlineMode, orderId],
    );

    const handleCollapseChange = useCallback(
        (key: string | string[]) => {
            dispatch(
                setCheckoutActiveCollapse(Array.isArray(key) ? key[0] : key),
            );
        },
        [dispatch],
    );

    const borderColor = useMemo(
        () =>
            isOfflineOrder
                ? "offline"
                : orderType === "h"
                  ? closed
                      ? "closed"
                      : "opened"
                  : orderType,
        [isOfflineOrder, orderType, closed],
    );

    return (
        <FlexContainer alignItems="flex-start">
            {!closed && (
                <StyledCACheckbox
                    onChange={handleCheckboxChange}
                    checked={isChosen}
                    isActive={!offlineMode}
                />
            )}
            <StyledCACollapse
                accordion
                activeKey={activeCollapse}
                borderColor={borderColor}
                onChange={handleCollapseChange}
            >
                <StyledCACollapse.Panel
                    key={`${orderId}-${index + 1}`}
                    header={
                        <CheckoutHeader
                            invoiceId={invoiceId}
                            copyCheckoutInfoToClipboard={handleCopyCheckoutInfo}
                            orderType={orderType}
                            orderDate={orderDate}
                            fop={contragent}
                            address={address}
                            totalSum={totalSum}
                        />
                    }
                >
                    <CheckoutBody
                        orderId={orderId}
                        marketId={marketId}
                        companyName={companyName}
                        address={address}
                        fop={contragent}
                        orderDate={orderDate}
                        shipmentDate={shipmentDate}
                        customShipmentDate={customShipmentDate}
                        paymentDate={paymentDate}
                        paymentForm={paymentForm}
                        orderType={orderType}
                        offlineMode={offlineMode}
                        payOnDeliveryComment={payOnDeliveryComment}
                        payOnDeliveryChecked={isPayOnDelivery}
                        totalSum={totalSum}
                        products={Object.values(products)}
                        handleResetCheckouts={handleResetCheckouts}
                        isCarrier={isCarrier}
                        isSelfDelivery={isSelfDelivery}
                        closed={closed}
                    />
                </StyledCACollapse.Panel>
            </StyledCACollapse>
        </FlexContainer>
    );
};

export default memo(CheckoutAccordion, (prevProps, nextProps) => {
    return (
        prevProps.closed === nextProps.closed &&
        prevProps.index === nextProps.index &&
        prevProps.invoiceId === nextProps.invoiceId &&
        prevProps.orderId === nextProps.orderId &&
        prevProps.marketId === nextProps.marketId &&
        prevProps.products === nextProps.products &&
        prevProps.isCarrier === nextProps.isCarrier &&
        prevProps.isSelfDelivery === nextProps.isSelfDelivery &&
        prevProps.isPayOnDelivery === nextProps.isPayOnDelivery &&
        prevProps.ignoreRemainings === nextProps.ignoreRemainings &&
        prevProps.isChosen === nextProps.isChosen &&
        prevProps.orderType === nextProps.orderType &&
        prevProps.paymentDate === nextProps.paymentDate &&
        prevProps.orderDate === nextProps.orderDate &&
        prevProps.shipmentDate === nextProps.shipmentDate &&
        prevProps.customShipmentDate === nextProps.customShipmentDate &&
        prevProps.address === nextProps.address &&
        prevProps.contragent === nextProps.contragent &&
        prevProps.companyName === nextProps.companyName &&
        prevProps.paymentForm === nextProps.paymentForm &&
        prevProps.payOnDeliveryComment === nextProps.payOnDeliveryComment &&
        prevProps.handleChangeCheckoutChoice ===
            nextProps.handleChangeCheckoutChoice &&
        prevProps.handleResetCheckouts === nextProps.handleResetCheckouts
    );
});
